// Right-hand collapsible panel with project settings.
// Renders inside the project-detail layout; uses a fixed slide-in drawer
// on mobile, a normal sidebar column on desktop.

import { useEffect, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { Building2, LayoutTemplate, Lock, Trash2, Users, UserPlus, X } from "lucide-preact";
import type {
  ProjectStatus,
  ProjectVisibility,
  ProjectWithCounts,
} from "@juuplan/shared";
import { PROJECT_STATUSES } from "@juuplan/shared";
import {
  PROJECT_STATUS_LABEL,
  COLOR_PRESETS,
  formatDateShort,
} from "../lib/format";
import { deleteProject, refreshProjects } from "../store/projects";
import { patchProject, refreshProjectDetail } from "../store/project";
import { saveProjectAsTemplate } from "../store/templates";
import { Field, INPUT_CLS } from "./NewProjectModal";
import { Modal } from "./Modal";
import { MarkdownEditor } from "./MarkdownEditor";
import { ExternalGuestsSection } from "./ExternalGuestsSection";
import { ShareDialog } from "./ShareDialog";
import { useMe } from "../lib/auth";
import { StatusesSection } from "./StatusesSection";
import { CustomFieldsSection } from "./CustomFieldsSection";
import { CyclesSection } from "./CyclesSection";
import { WorkItemTypesSection } from "./WorkItemTypesSection";
import { AutomationRulesSection } from "./AutomationRulesSection";
import { IntakeFormsSection } from "./IntakeFormsSection";
import { LinkedRepositoriesSection } from "./LinkedRepositoriesSection";
import { useSpaces, refreshSpaces } from "../store/spaces";
import { useFolders } from "../store/folders";
import { moveProject } from "../lib/api";
import { toastSuccess, toastError } from "../lib/toast";

import { Button } from "./ui/Button";

interface Props {
  project: ProjectWithCounts;
  /** Optional section to scroll into view on open (e.g. "git"). */
  scrollToSection?: string;
  onClose: () => void;
}

export function ProjectSettingsPanel(props: Props): preact.JSX.Element {
  const { project, scrollToSection, onClose } = props;
  const me = useMe();
  const gitSectionRef = useRef<HTMLDivElement>(null);

  // Deep-link: scroll the requested section into view once after open.
  useEffect(() => {
    if (scrollToSection === "git" && gitSectionRef.current) {
      gitSectionRef.current.scrollIntoView({ block: "start" });
    }
  }, [scrollToSection]);
  const [, setLocation] = useLocation();
  const [description, setDescription] = useState(project.description ?? "");
  const [savingDesc, setSavingDesc] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [movingSpace, setMovingSpace] = useState(false);
  const [movingFolder, setMovingFolder] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const spaces = useSpaces();
  const folders = useFolders(project.space_id);

  // Schritt 1 Sharing: Owner/Admin dürfen teilen + Sichtbarkeit ändern.
  const canManage = project.my_role === "owner" || !!me.user?.is_admin;

  useEffect(() => {
    setDescription(project.description ?? "");
  }, [project.id]);

  // Fire-and-forget PATCH für die Quick-Toggles unten (Status/Sichtbarkeit/
  // Farbe/Datumsfelder): `patchProject` selbst faengt nichts ab, und ein
  // `void patchProject(...)` ohne `.catch` waere eine unbehandelte Ablehnung.
  // `api()` toastet PATCH-Fehler bereits selbst (lib/api.ts: schreibende
  // Methoden) — hier nur den Rejection-Pfad abschliessen, kein zweiter Toast.
  const patchProjectField = (patch: Partial<ProjectWithCounts>): void => {
    void patchProject(project.id, patch).catch(() => {
      /* api() toastet bereits */
    });
  };

  const setVisibility = (v: ProjectVisibility): void => {
    if (v === project.visibility) return;
    patchProjectField({ visibility: v });
  };

  const commitDescription = async (): Promise<void> => {
    setSavingDesc(true);
    try {
      await patchProject(project.id, { description: description || null });
    } finally {
      setSavingDesc(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    await deleteProject(project.id);
    setLocation("/");
  };

  const handleMoveProject = async (spaceId: number): Promise<void> => {
    setMovingSpace(true);
    try {
      await moveProject(project.id, { space_id: spaceId });
      await Promise.all([refreshProjectDetail(), refreshProjects(), refreshSpaces()]);
      toastSuccess("Projekt verschoben.");
    } catch {
      toastError("Projekt konnte nicht verschoben werden.");
    } finally {
      setMovingSpace(false);
    }
  };

  const handleMoveFolder = async (folderId: number | null): Promise<void> => {
    setMovingFolder(true);
    try {
      await moveProject(project.id, { space_id: project.space_id, folder_id: folderId });
      await Promise.all([refreshProjectDetail(), refreshProjects()]);
      toastSuccess(folderId ? "Ordner zugewiesen." : "Aus Ordner entfernt.");
    } catch {
      toastError("Ordner konnte nicht zugewiesen werden.");
    } finally {
      setMovingFolder(false);
    }
  };

  const canEdit =
    project.my_role === "owner" ||
    project.my_role === "editor" ||
    !!me.user?.is_admin;

  return (
    <aside class="fixed lg:sticky top-0 right-0 z-30 h-dvh w-full sm:w-[360px] shrink-0 border-l border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] flex flex-col">
      <header class="flex items-center justify-between gap-2 px-4 py-3 border-b border-[color:var(--color-outline-variant)]">
        <h2 class="text-sm font-semibold text-[color:var(--color-on-surface)] min-w-0 truncate">
          Projekt-Einstellungen
        </h2>
        <button
          type="button"
          onClick={onClose}
          class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors shrink-0"
          aria-label="Einstellungen schließen"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </header>

      {/* Scrolls vertically only, like Modal (#855) — every section has to fit
          the panel width, so a sideways scroll would only ever hide content. */}
      <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 flex flex-col gap-4">
        <Field label="Status" htmlFor="ps-status">
          <select
            id="ps-status"
            value={project.status}
            onChange={(e) =>
              patchProjectField({
                status: (e.target as HTMLSelectElement).value as ProjectStatus,
              })
            }
            class={INPUT_CLS}
          >
            {PROJECT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PROJECT_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </Field>

        {/* Schritt 1 Sharing: Sichtbarkeit + Teilen — nur Owner/Admin. */}
        <Field label="Sichtbarkeit" htmlFor="ps-visibility">
          <div
            id="ps-visibility"
            class="inline-flex w-full rounded border border-[color:var(--color-outline-variant)] overflow-hidden"
            role="group"
            aria-label="Projekt-Sichtbarkeit"
          >
            {(
              [
                { value: "private", label: "Privat", icon: <Lock size={14} aria-hidden="true" /> },
                { value: "shared", label: "Geteilt", icon: <Users size={14} aria-hidden="true" /> },
                { value: "org", label: "Organisation", icon: <Building2 size={14} aria-hidden="true" /> },
              ] as { value: ProjectVisibility; label: string; icon: preact.JSX.Element }[]
            ).map((opt) => {
              const active = project.visibility === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={!canManage}
                  onClick={() => setVisibility(opt.value)}
                  aria-pressed={active}
                  class={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium transition-colors min-h-[32px] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] ${
                    active
                      ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
                      : "bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                  }`}
                  title={opt.label}
                >
                  {opt.icon}
                  <span class="hidden sm:inline">{opt.label}</span>
                </button>
              );
            })}
          </div>
          {/* Hinweis beim Wechsel auf Privat — Nicht-Member verlieren Zugriff. */}
          {project.visibility === "private" && (
            <p class="mt-1.5 text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] rounded px-2 py-1.5">
              <strong>Privat:</strong> Nur der Owner und explizit freigegebene
              Personen haben Zugriff. Alle anderen verlieren den Zugriff.
            </p>
          )}
          {canManage && (
            <button
              type="button"
              onClick={() => setShowShare(true)}
              class="mt-2 inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
            >
              <UserPlus size={14} aria-hidden="true" />
              Teilen{project.member_count > 0 ? ` (${project.member_count})` : ""}
            </button>
          )}
        </Field>

        <Field label="Beschreibung" htmlFor="ps-desc">
          <MarkdownEditor
            id="ps-desc"
            value={description}
            onChange={setDescription}
            rows={5}
            placeholder="Projektbeschreibung in Markdown…"
          />
          <div class="flex items-center justify-end">
            <Button
              variant="primary"
              size="md"
              type="button"
              onClick={() => void commitDescription()}
              disabled={savingDesc || description === (project.description ?? "")}
            >
              {savingDesc ? "Speichere…" : "Speichern"}
            </Button>
          </div>
        </Field>

        {/* B4: Move to space — only show when there are other editor spaces available */}
        {spaces.items.filter((sp) => sp.my_role === "editor" && sp.id !== project.space_id).length > 0 && (
          <Field label="In Space verschieben" htmlFor="ps-move-space">
            <select
              id="ps-move-space"
              value=""
              disabled={movingSpace}
              onChange={(e) => {
                const val = (e.target as HTMLSelectElement).value;
                if (val) void handleMoveProject(parseInt(val, 10));
              }}
              class={INPUT_CLS}
            >
              <option value="">Space wählen…</option>
              {spaces.items
                .filter((sp) => sp.my_role === "editor" && sp.id !== project.space_id)
                .map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {sp.icon ? `${sp.icon} ` : ""}{sp.name}
                  </option>
                ))}
            </select>
            {movingSpace && (
              <p class="text-label text-[color:var(--color-on-surface-variant)] mt-1">Verschiebe…</p>
            )}
          </Field>
        )}

        {/* H2d: Folder assignment within current space */}
        <Field label="Ordner" htmlFor="ps-folder">
          {canEdit ? (
            <>
              <select
                id="ps-folder"
                value={project.folder_id ?? ""}
                disabled={movingFolder || folders.loading}
                onChange={(e) => {
                  const val = (e.target as HTMLSelectElement).value;
                  void handleMoveFolder(val ? parseInt(val, 10) : null);
                }}
                class={INPUT_CLS}
              >
                <option value="">Kein Ordner</option>
                {folders.items.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
              {movingFolder && (
                <p class="text-label text-[color:var(--color-on-surface-variant)] mt-1">Speichere…</p>
              )}
            </>
          ) : (
            <p class="text-sm text-[color:var(--color-on-surface)]">
              {folders.loading
                ? "Lädt…"
                : (folders.items.find((f) => f.id === project.folder_id)?.name ?? "Kein Ordner")}
            </p>
          )}
        </Field>

        <Field label="Farbe" htmlFor="ps-color">
          <div id="ps-color" class="flex items-center gap-1.5 flex-wrap">
            {COLOR_PRESETS.map((c) => {
              const active = project.color === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => patchProjectField({ color: c })}
                  class={`size-7 rounded border-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
                    active ? "border-[color:var(--color-on-surface)]" : "border-transparent"
                  }`}
                  style={{ background: c }}
                  aria-label={`Farbe ${c}`}
                  aria-pressed={active}
                />
              );
            })}
          </div>
        </Field>

        <div class="grid grid-cols-2 gap-2">
          <Field label="Startdatum" htmlFor="ps-start">
            <input
              id="ps-start"
              type="date"
              value={project.start_date ?? ""}
              onChange={(e) =>
                patchProjectField({
                  start_date: (e.target as HTMLInputElement).value || null,
                })
              }
              class={INPUT_CLS}
            />
          </Field>
          <Field label="Zieldatum" htmlFor="ps-target">
            <input
              id="ps-target"
              type="date"
              value={project.target_date ?? ""}
              onChange={(e) =>
                patchProjectField({
                  target_date: (e.target as HTMLInputElement).value || null,
                })
              }
              class={INPUT_CLS}
            />
          </Field>
        </div>

        {/* A4: Per-project custom status management */}
        <div class="pt-3 border-t border-[color:var(--color-outline-variant)]">
          <StatusesSection
            projectId={project.id}
            canEdit={project.my_role === "owner" || project.my_role === "editor" || !!me.user?.is_admin}
          />
        </div>

        {/* D4: Per-project custom field definitions */}
        <div class="pt-3 border-t border-[color:var(--color-outline-variant)]">
          <CustomFieldsSection
            projectId={project.id}
            canEdit={project.my_role === "owner" || project.my_role === "editor" || !!me.user?.is_admin}
          />
        </div>

        {/* F3: Per-project cycle management */}
        <div class="pt-3 border-t border-[color:var(--color-outline-variant)]">
          <CyclesSection
            projectId={project.id}
            canEdit={project.my_role === "owner" || project.my_role === "editor" || !!me.user?.is_admin}
          />
        </div>

        {/* E6: Per-project work item type management */}
        <div class="pt-3 border-t border-[color:var(--color-outline-variant)]">
          <WorkItemTypesSection
            projectId={project.id}
            canEdit={project.my_role === "owner" || project.my_role === "editor" || !!me.user?.is_admin}
          />
        </div>

        {/* I3b: Per-project automation rules */}
        <div class="pt-3 border-t border-[color:var(--color-outline-variant)]">
          <AutomationRulesSection
            projectId={project.id}
            canEdit={project.my_role === "owner" || project.my_role === "editor" || !!me.user?.is_admin}
          />
        </div>

        {/* G3a: Per-project intake forms */}
        <div class="pt-3 border-t border-[color:var(--color-outline-variant)]">
          <IntakeFormsSection
            projectId={project.id}
            canEdit={project.my_role === "owner" || project.my_role === "editor" || !!me.user?.is_admin}
            phases={project.phases}
          />
        </div>

        {/* P3b: Per-project linked repositories */}
        <div ref={gitSectionRef} class="pt-3 border-t border-[color:var(--color-outline-variant)]">
          <LinkedRepositoriesSection
            projectId={project.id}
            canEdit={project.my_role === "owner" || project.my_role === "editor" || !!me.user?.is_admin}
          />
        </div>

        <div class="text-label text-[color:var(--color-on-surface-variant)] space-y-1 pt-2 border-t border-[color:var(--color-outline-variant)]">
          <p>Erstellt: {formatDateShort(project.created_at)}</p>
          <p>Geändert: {formatDateShort(project.updated_at)}</p>
          <p>
            Phasen: {project.phases.length} · Aufgaben: {project.tasks_done}/
            {project.tasks_total}
          </p>
        </div>

        {/* Save as template */}
        <div class="pt-3 border-t border-[color:var(--color-outline-variant)]">
          <button
            type="button"
            onClick={() => setShowSaveTemplate(true)}
            class="inline-flex items-center gap-1.5 rounded px-3 py-2 text-xs text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] transition-colors"
          >
            <LayoutTemplate size={14} aria-hidden="true" />
            Als Template speichern
          </button>
        </div>

        <div class="pt-3 border-t border-[color:var(--color-outline-variant)]">
          {confirmDel ? (
            <div class="flex flex-col gap-2">
              <p class="text-xs text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-3 py-2">
                „{project.name}" in den Papierkorb verschieben? Phasen und
                Aufgaben wandern mit.
              </p>
              <div class="flex items-center gap-2">
                <Button
                  variant="destructive"
                  size="md"
                  class="flex-1"
                  type="button"
                  onClick={() => void onDelete()}
                >
                  In den Papierkorb verschieben
                </Button>
                <button
                  type="button"
                  onClick={() => setConfirmDel(false)}
                  class="rounded border border-[color:var(--color-outline-variant)] px-3 py-2 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDel(true)}
              class="inline-flex items-center gap-1.5 rounded px-3 py-2 text-xs text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] transition-colors"
            >
              <Trash2 size={14} aria-hidden="true" />
              In den Papierkorb verschieben
            </button>
          )}
        </div>

        {/* Sprint 3 Story 4: externe Gäste verwalten — nur für interne User */}
        {!me.user?.is_external && (
          <div class="mt-2">
            <ExternalGuestsSection projectId={project.id} />
          </div>
        )}
      </div>

      {showShare && (
        <ShareDialog project={project} onClose={() => setShowShare(false)} />
      )}

      {showSaveTemplate && (
        <SaveAsTemplateDialog
          project={project}
          onClose={() => setShowSaveTemplate(false)}
        />
      )}
    </aside>
  );
}

// ---------- Save-as-template dialog ----------

interface SaveAsTemplateDialogProps {
  project: ProjectWithCounts;
  onClose: () => void;
}

function SaveAsTemplateDialog(props: SaveAsTemplateDialogProps): preact.JSX.Element {
  const { project, onClose } = props;
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    if (project.phases.length === 0) {
      toastError("Das Projekt hat keine Phasen — Template benötigt mindestens eine Phase.");
      return;
    }

    setSubmitting(true);
    try {
      // Server reads the project's current phases + tasks and converts dates
      // to relative day-offsets — no client-side payload assembly.
      await saveProjectAsTemplate(project.id, {
        name: trimmedName,
        description: description.trim() || undefined,
      });
      toastSuccess(`Template „${trimmedName}" gespeichert.`);
      onClose();
    } catch {
      // api wrapper already toasted
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Als Template speichern" onClose={onClose} size="sm">
      <form onSubmit={onSubmit} class="flex flex-col gap-4">
        <Field label="Template-Name" htmlFor="st-name" required>
          <input
            id="st-name"
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            required
            maxLength={200}
            autoFocus
            class={INPUT_CLS}
          />
        </Field>

        <Field label="Beschreibung" htmlFor="st-desc">
          <input
            id="st-desc"
            type="text"
            value={description}
            onInput={(e) => setDescription((e.target as HTMLInputElement).value)}
            maxLength={2000}
            class={INPUT_CLS}
            placeholder="Optionale Kurzbeschreibung"
          />
        </Field>

        <p class="text-label text-[color:var(--color-on-surface-variant)]">
          {project.phases.length} Phasen werden gespeichert.
          Titel, Priorität, Meilensteine und der zeitliche Abstand der Aufgaben
          zueinander werden übernommen (relativ, nicht als feste Daten);
          Zuweisungen, Status und Ticket-Nummern werden nicht kopiert.
        </p>

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-[color:var(--color-outline-variant)] -mx-5 -mb-4 px-5 pb-4 mt-2">
          <button
            type="button"
            onClick={onClose}
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-2 text-sm font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
          >
            Abbrechen
          </button>
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={submitting || !name.trim()}
          >
            {submitting ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
